import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
  getMeetingAttendees,
  addMeetingAttendee,
  updateMeetingAttendee,
} from "../api/meeting-api";
import type {
  MeetingAttendee,
  AddAttendeeRequest,
  UpdateAttendeeRequest,
} from "../types/meeting.type";

const STALE_TIME = 30_000;

const meetingAttendeesKeys = {
  attendees: (rfxId: number, evalId: number) =>
    ["meetingAttendees", rfxId, evalId] as const,
};

interface UpdateAttendeeVariables {
  attendeeId: number;
  payload: UpdateAttendeeRequest;
}

interface UseMeetingAttendeesResult {
  attendees: MeetingAttendee[];
  isLoading: boolean;
  addAttendee: UseMutationResult<MeetingAttendee, Error, AddAttendeeRequest>;
  updateAttendee: UseMutationResult<MeetingAttendee, Error, UpdateAttendeeVariables>;
}

interface UseMeetingAttendeesOptions {
  enabled?: boolean;
}

export const useMeetingAttendees = (
  rfxId: number | undefined,
  evalId: number | undefined,
  options: UseMeetingAttendeesOptions = {}
): UseMeetingAttendeesResult => {
  const { enabled: externalEnabled = true } = options;
  const queryClient = useQueryClient();

  const query: UseQueryResult<MeetingAttendee[]> = useQuery({
    queryKey: meetingAttendeesKeys.attendees(rfxId!, evalId!),
    queryFn: () => getMeetingAttendees(rfxId!, evalId!),
    enabled: !!rfxId && !!evalId && externalEnabled,
    staleTime: STALE_TIME,
  });

  const invalidateAttendees = () => {
    queryClient.invalidateQueries({
      queryKey: meetingAttendeesKeys.attendees(rfxId!, evalId!),
    });
  };

  const addAttendeeMutation = useMutation({
    mutationFn: (payload: AddAttendeeRequest) =>
      addMeetingAttendee(rfxId!, evalId!, payload),
    onSuccess: invalidateAttendees,
  });

  const updateAttendeeMutation = useMutation({
    mutationFn: ({ attendeeId, payload }: UpdateAttendeeVariables) =>
      updateMeetingAttendee(rfxId!, evalId!, attendeeId, payload),
    onSuccess: invalidateAttendees,
  });

  return {
    attendees: query.data ?? [],
    isLoading: query.isLoading,
    addAttendee: addAttendeeMutation,
    updateAttendee: updateAttendeeMutation,
  };
};
